import type { ReactNode } from 'react';

import { LAND_COVER_LEGEND_SWATCH } from '@/features/map/utils/landCoverCategoryColors';
import type { AoiLandCoverCategoryKey } from '@/types/flood.types';

export interface LandCoverPercentBarRowProps {
  categoryKey: AoiLandCoverCategoryKey;
  /** i18n label (tên lớp). */
  label: ReactNode;
  areaHa: number;
  pctOfTotal: number | null;
  /** Bản tab Tác động — khoảng cách nhỏ hơn. */
  compact?: boolean;
}

/**
 * Một dòng: tên lớp + ha + thanh % ngang (màu theo legend).
 */
export function LandCoverPercentBarRow({
  categoryKey,
  label,
  areaHa,
  pctOfTotal,
  compact = false,
}: LandCoverPercentBarRowProps) {
  const pct = pctOfTotal != null ? Math.min(100, Math.max(0, pctOfTotal)) : null;
  const fill = LAND_COVER_LEGEND_SWATCH[categoryKey];
  const gap = compact ? 'gap-y-1' : 'gap-y-1.5';
  const barH = compact ? 'h-2' : 'h-2.5';
  const labelSize = compact ? 'text-[12px]' : 'text-[11px]';

  return (
    <div className={`flex flex-col ${gap}`}>
      <div className={`flex items-start justify-between gap-2 ${labelSize}`}>
        <span className="flex min-w-0 items-center gap-1.5 text-slate-700 dark:text-slate-200">
          <span
            className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-sm border border-white/15 shadow-sm"
            style={{ backgroundColor: fill }}
            aria-hidden
          />
          <span className="truncate font-medium">{label}</span>
        </span>
        <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
          {areaHa.toFixed(1)} ha
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div
          className={`relative ${barH} min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200/90 shadow-inner ring-1 ring-inset ring-slate-900/5 dark:bg-slate-700/90 dark:ring-white/10`}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct ?? 0}
          aria-label={typeof label === 'string' ? label : undefined}
        >
          {pct != null && pct > 0 ? (
            <div
              className={`${barH} rounded-full shadow-sm transition-[width] duration-300 ease-out`}
              style={{
                width: `${pct}%`,
                backgroundColor: fill,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
              }}
            />
          ) : null}
        </div>
        <span className="w-11 shrink-0 text-right text-[11px] font-semibold tabular-nums text-slate-800 dark:text-slate-100">
          {pct != null ? `${pctOfTotal}%` : '—'}
        </span>
      </div>
    </div>
  );
}
